import queryString from 'query-string';

import PageUtil from 'core/components/layout/PageUtil';
import initialState from 'core/reducers/initialState';
import { iDef } from 'core/util/TypeUtils';
import omit from 'lodash/omit';
import ConfigUtil from './config/ConfigUtil';
import LayoutUtil from './LayoutUtil';
import StringUtil from "./StringUtil";

export default class UrlUtil {
	public static PARAM_INTERNAL_TOKEN = "internaltoken";
	
	public static getBasePath(): string {
		if(typeof process.env.CW_ENV === "object") {
			return process.env.CW_ENV.path;
		}
		return ConfigUtil.getEnvConfig().appPath;
	}

	public static getAppPath(): string {
		const path: string = UrlUtil.getBasePath();
		return path.endsWith("/") ? path : path + "/";
	}

	public static isAppPath(path: string): boolean {
		return UrlUtil.getAppPath() === path;
	}

	/**
	 * Return full path form directories accounting application base path
	 * Accepts list of directories as arguments or an array of directories
	 *
	 * Example:
	 *
	 * getLink('users', 'bob') -> {appPath}/users/bob
	 * getLink(['users', 'bob']) -> {appPath}/users/bob
	 *
	 */
	public static getLink(...args: any[]): string {
		const path: string = UrlUtil.getBasePath();
		let paths: string[] = [];

		if (args.length > 0) {
			if (Array.isArray(arguments[0])) {
				paths = arguments[0];
			} else {
				// filter undefined and null args. allow boolean and integer
				paths = Array.from(arguments).filter((a) => iDef(a) && a !== null);
			}

			const href = paths.join("/");

			return href.startsWith("/") ? path + href.substring(1) : path + href;
		}
	}

	public static getPagePathLink(store: typeof initialState, pagePath: string[] = []): string {
		const parts: string[] = [];

		for (let i = 0, len = pagePath.length; i < len; i++) {
			const panel = store.config.panels[pagePath[i]];
			if (!panel) {
				break;
			}
			// don't add undefined, empty, or
			if (i < 1 || panel.path) {
				parts.push(panel.path);
			}
		}

		return this.getLink(...parts);
	}

	public static isPagePathActive(store: any, pagePath: string[], markActiveForChild:boolean = false): boolean {
		const pathname = this.getPagePathLink(store, pagePath);
		const currentPath = store.router.location.pathname;
		let appPath = ConfigUtil.getEnvConfig().appPath;
		if (markActiveForChild) {
			if (localStorage.getItem("page-path")) {
				let page = JSON.parse(localStorage.getItem("page-path"))
				if (page && page.pagePath && page.pagePath.length) {
					page = [page.pagePath[0]]
					const parentPath = this.getPagePathLink(store, page);
					if (parentPath && parentPath != appPath) {
						return currentPath && pathname && (pathname as string).startsWith(parentPath);
					}
				}
			}
			return currentPath && pathname && (currentPath as string).startsWith(pathname);
		} else{
			return currentPath === pathname;
		}
	}

	public static getActivePageInstance(store: any): string | null {

		const { pathname } = store.router.location;

		let activePageInstance = null;

		LayoutUtil.visitLayout(store, (panel, instance, _, ancestors) => {
			if (panel.id !== PageUtil.PAGE_ID) {
				return true;
			}

			const path = this.getPagePathLink(store, [...ancestors, instance]);

			if (path === pathname) {
				activePageInstance = instance;
				return false;
			}
			return true;
		});

		return activePageInstance;
	}

	public static getSearch(): string {
		return window.location.search;
	}

	public static getSearchParams(): { [key: string]: string; } {
		return queryString.parse(UrlUtil.getSearch()) || {};
	}

	public static getSearchParam(key: string, defaultValue?: string): string {
		const params = UrlUtil.getSearchParams();

		if (iDef(params) && iDef(params[key])) {
			return params[key];
		}

		return defaultValue;
	}

	public static getCurrentPath(excludeAppPath?: boolean) {
		let path = window.location.pathname;
		if (excludeAppPath && path.indexOf(UrlUtil.getAppPath()) > -1) {
			path = path.slice(UrlUtil.getAppPath().length);
		}

		if (path.indexOf('?') > -1) {
			path = path.slice(0, path.indexOf('?'));
		}

		return path.startsWith("/") ? path : "/" + path;
	}

	public static getCurrentParams(includeInternal: boolean = false): { [key: string]: string; } {
		let params = queryString.parse(window.location.search);

		if (!includeInternal) {
			params = omit(params, ["token", "v"]);
		}

		return params;
	}

	public static getGatewayRedirectLink(path: string, params: object) {
		let gatewayUri = ConfigUtil.getEnvConfig().gatewayUri;

		// make url safe
		let encoded = btoa(window.location.href);
		encoded = StringUtil.replaceAll(encoded, "/", "_");
		encoded = StringUtil.replaceAll(encoded, "=", "-");

		gatewayUri += `?uiReferral=${encoded}`;

		if(!params["noForward"]) {
			const forwardUri = "/ui?" + queryString.stringify({ path: path || "", ...params || {} });
			gatewayUri += (`&f=${UrlUtil.encode(forwardUri, true)}`);
		}

		return gatewayUri;
	}

	public static getAuthorizedLink(token: string, destination: string) {
		const gatewayUri = ConfigUtil.getEnvConfig().gatewayUri;
		return gatewayUri + `?internaltoken=${token}&f=${UrlUtil.encode(destination)}&embed=true`;
	}

	/**
	 * see com.digitalignite.core.web.util.UrlUtil in backend project
	 * @param str 		string to encode
	 * @param manual 	replace all of the %'s with *'s so clients don't double encode
	 */
	public static encode(str: string, manual: boolean = false): string {
		if (!str) { return str; }

		let retval = encodeURIComponent(str);

		if (manual) {
			if (retval.indexOf('*') > -1) {
				retval = retval.replace(/\*/g, '%2A');
			}
			while (retval.indexOf('%') > -1) {
				retval = retval.replace('%', '*');
			}
		}

		return retval;
	}

	/**
	 * It will return the url string without key
	 * @param sourceURL
	 * @param key
	 */
	public static removeUrlParam = (sourceURL: string, key: string) => {
		let url = sourceURL.split('?')[0],
			param,
			params_arr = [],
			queryString =
				sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';
		if (queryString !== '') {
			params_arr = queryString.split('&');
			for (let i = params_arr.length - 1; i >= 0; i -= 1) {
				param = params_arr[i].split('=')[0];
				if (param === key) {
					params_arr.splice(i, 1);
				}
			}
			url = url + '?' + params_arr.join('&');
		}
		return url;
	};

	public static extractDomain = () => {
		let url= window.location.href;
		let domain: string;
		//find & remove protocol (http, ftp, etc.) and get domain
		if (url.indexOf("://") > -1) {
		  domain = url.split('/')[2];
		}
		else {
		  domain = url.split('/')[0];
		}
		
		//find & remove www
		if (domain.indexOf("www.") > -1) { 
		  domain = domain.split('www.')[1];
		}
		
		domain = domain.split(':')[0]; //find & remove port number
		domain = domain.split('?')[0]; //find & remove url params
	  
		return domain;
	  }
	
	  public static getKeepAliveUri = () => ConfigUtil.getEnvConfig().keepaliveUri;	

}



// WEBPACK FOOTER //
// ./src/core/util/UrlUtil.ts